import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Upload, Camera, Search, ExternalLink, X, Heart, Share2, History, Users, Baby, Store, Loader2
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import axios from "axios";
import CameraCapture from "@/components/Camera";

// Retailer options - Top retailers, brands, and marketplaces
const RETAILER_OPTIONS = [
  { value: "all", label: "Search All Retailers" },
  // Major Marketplaces
  { value: "amazon", label: "Amazon", category: "Marketplaces" },
  { value: "ebay", label: "eBay", category: "Marketplaces" },
  { value: "walmart", label: "Walmart", category: "Marketplaces" },
  { value: "target", label: "Target", category: "Marketplaces" },
  { value: "etsy", label: "Etsy", category: "Marketplaces" },
  { value: "zappos", label: "Zappos", category: "Marketplaces" },
  { value: "poshmark", label: "Poshmark", category: "Marketplaces" },
  { value: "mercari", label: "Mercari", category: "Marketplaces" },
  { value: "thredup", label: "ThredUp", category: "Marketplaces" },
  // Department Stores
  { value: "nordstrom", label: "Nordstrom", category: "Department Stores" },
  { value: "macys", label: "Macy's", category: "Department Stores" },
  { value: "bloomingdales", label: "Bloomingdale's", category: "Department Stores" },
  { value: "saks", label: "Saks Fifth Avenue", category: "Department Stores" },
  { value: "neiman marcus", label: "Neiman Marcus", category: "Department Stores" },
  { value: "jcpenney", label: "JCPenney", category: "Department Stores" },
  { value: "kohls", label: "Kohl's", category: "Department Stores" },
  { value: "dillards", label: "Dillard's", category: "Department Stores" },
  { value: "belk", label: "Belk", category: "Department Stores" },
  { value: "nordstrom rack", label: "Nordstrom Rack", category: "Department Stores" },
  // Fast Fashion
  { value: "zara", label: "Zara", category: "Fast Fashion" },
  { value: "h&m", label: "H&M", category: "Fast Fashion" },
  { value: "forever 21", label: "Forever 21", category: "Fast Fashion" },
  { value: "shein", label: "SHEIN", category: "Fast Fashion" },
  { value: "asos", label: "ASOS", category: "Fast Fashion" },
  { value: "boohoo", label: "Boohoo", category: "Fast Fashion" },
  { value: "fashion nova", label: "Fashion Nova", category: "Fast Fashion" },
  { value: "uniqlo", label: "Uniqlo", category: "Fast Fashion" },
  { value: "mango", label: "Mango", category: "Fast Fashion" },
  { value: "primark", label: "Primark", category: "Fast Fashion" },
  // Sportswear & Athletic
  { value: "nike", label: "Nike", category: "Sportswear" },
  { value: "adidas", label: "Adidas", category: "Sportswear" },
  { value: "puma", label: "Puma", category: "Sportswear" },
  { value: "under armour", label: "Under Armour", category: "Sportswear" },
  { value: "lululemon", label: "Lululemon", category: "Sportswear" },
  { value: "athleta", label: "Athleta", category: "Sportswear" },
  { value: "fabletics", label: "Fabletics", category: "Sportswear" },
  { value: "reebok", label: "Reebok", category: "Sportswear" },
  { value: "new balance", label: "New Balance", category: "Sportswear" },
  { value: "gym shark", label: "Gymshark", category: "Sportswear" },
  // Premium & Designer
  { value: "hugo boss", label: "Hugo Boss", category: "Premium Brands" },
  { value: "ralph lauren", label: "Ralph Lauren", category: "Premium Brands" },
  { value: "tommy hilfiger", label: "Tommy Hilfiger", category: "Premium Brands" },
  { value: "calvin klein", label: "Calvin Klein", category: "Premium Brands" },
  { value: "michael kors", label: "Michael Kors", category: "Premium Brands" },
  { value: "coach", label: "Coach", category: "Premium Brands" },
  { value: "kate spade", label: "Kate Spade", category: "Premium Brands" },
  { value: "tory burch", label: "Tory Burch", category: "Premium Brands" },
  { value: "ted baker", label: "Ted Baker", category: "Premium Brands" },
  { value: "burberry", label: "Burberry", category: "Premium Brands" },
  // Luxury
  { value: "gucci", label: "Gucci", category: "Luxury" },
  { value: "louis vuitton", label: "Louis Vuitton", category: "Luxury" },
  { value: "prada", label: "Prada", category: "Luxury" },
  { value: "versace", label: "Versace", category: "Luxury" },
  { value: "balenciaga", label: "Balenciaga", category: "Luxury" },
  { value: "net-a-porter", label: "Net-A-Porter", category: "Luxury" },
  { value: "farfetch", label: "Farfetch", category: "Luxury" },
  { value: "ssense", label: "SSENSE", category: "Luxury" },
  { value: "mytheresa", label: "Mytheresa", category: "Luxury" },
  { value: "matches fashion", label: "MATCHESFASHION", category: "Luxury" },
  // Casual & Lifestyle
  { value: "gap", label: "Gap", category: "Casual" },
  { value: "old navy", label: "Old Navy", category: "Casual" },
  { value: "banana republic", label: "Banana Republic", category: "Casual" },
  { value: "j.crew", label: "J.Crew", category: "Casual" },
  { value: "american eagle", label: "American Eagle", category: "Casual" },
  { value: "abercrombie", label: "Abercrombie & Fitch", category: "Casual" },
  { value: "hollister", label: "Hollister", category: "Casual" },
  { value: "express", label: "Express", category: "Casual" },
  { value: "urban outfitters", label: "Urban Outfitters", category: "Casual" },
  { value: "anthropologie", label: "Anthropologie", category: "Casual" },
  { value: "free people", label: "Free People", category: "Casual" },
  // Outdoor & Workwear
  { value: "patagonia", label: "Patagonia", category: "Outdoor" },
  { value: "north face", label: "The North Face", category: "Outdoor" },
  { value: "columbia", label: "Columbia", category: "Outdoor" },
  { value: "carhartt", label: "Carhartt", category: "Outdoor" },
  { value: "rei", label: "REI", category: "Outdoor" },
  { value: "ll bean", label: "L.L.Bean", category: "Outdoor" },
  { value: "timberland", label: "Timberland", category: "Outdoor" },
  // Men's Specific
  { value: "mens wearhouse", label: "Men's Wearhouse", category: "Men's" },
  { value: "jos a bank", label: "Jos. A. Bank", category: "Men's" },
  { value: "bonobos", label: "Bonobos", category: "Men's" },
  { value: "brooks brothers", label: "Brooks Brothers", category: "Men's" },
  { value: "charles tyrwhitt", label: "Charles Tyrwhitt", category: "Men's" },
  // Women's Specific  
  { value: "revolve", label: "Revolve", category: "Women's" },
  { value: "shopbop", label: "Shopbop", category: "Women's" },
  { value: "reformation", label: "Reformation", category: "Women's" },
  { value: "everlane", label: "Everlane", category: "Women's" },
  { value: "madewell", label: "Madewell", category: "Women's" },
  { value: "ann taylor", label: "Ann Taylor", category: "Women's" },
  { value: "loft", label: "LOFT", category: "Women's" },
  // Value & Discount
  { value: "ross", label: "Ross Dress for Less", category: "Value" },
  { value: "tj maxx", label: "TJ Maxx", category: "Value" },
  { value: "marshalls", label: "Marshalls", category: "Value" },
  { value: "burlington", label: "Burlington", category: "Value" },
  { value: "6pm", label: "6pm", category: "Value" },
];

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Gender options for search
const GENDER_OPTIONS = [
  { value: "all", label: "All Genders" },
  { value: "women", label: "Women" },
  { value: "men", label: "Men" },
  { value: "unisex", label: "Unisex" }
];

// Age group options for search
const AGE_GROUP_OPTIONS = [
  { value: "all", label: "All Ages" },
  { value: "infant", label: "Infant (0-12 months)" },
  { value: "toddler", label: "Toddler (1-3 years)" },
  { value: "kids", label: "Kids (4-12 years)" },
  { value: "teens", label: "Teens (13-17 years)" },
  { value: "adults", label: "Adults (18+)" }
];

// Get match quality label helper
const getMatchLabel = (colorType) => {
  switch(colorType) {
    case 'exact': return { text: 'Excellent Match', className: 'match-excellent' };
    case 'complementary': return { text: 'Complementary', className: 'match-close' };
    default: return { text: 'Similar Tone', className: 'match-similar' };
  }
};

// Product Card Component - Editorial Style with Share (moved outside Dashboard)
const ProductCard = ({ result, category, wishlistItems, trackClick, addToWishlist, onShare }) => {
  const [isLoadingUrl, setIsLoadingUrl] = useState(false);
  const matchLabel = getMatchLabel(result.color_type);
  
  const handleViewItem = async (e) => {
    e.preventDefault();
    
    // If we have a product_token, fetch direct URL first (lazy loading)
    if (result.product_token && !result.has_direct_url) {
      setIsLoadingUrl(true);
      try {
        const response = await axios.post(`${API}/product/direct-url`, {
          product_token: result.product_token,
          fallback_url: result.url
        });
        
        if (response.data.success && response.data.direct_url) {
          // Track the click
          trackClick({ ...result, url: response.data.direct_url });
          // Open direct URL
          window.open(response.data.direct_url, '_blank', 'noopener,noreferrer');
        }
      } catch (error) {
        console.error("Failed to get direct URL, using fallback");
        trackClick(result);
        window.open(result.affiliate_url || result.url, '_blank', 'noopener,noreferrer');
      } finally {
        setIsLoadingUrl(false);
      }
    } else {
      // No product_token or already has direct URL - use existing URL
      trackClick(result);
      window.open(result.affiliate_url || result.url, '_blank', 'noopener,noreferrer');
    }
  };
  
  const handleShare = async () => {
    const shareData = {
      title: result.title,
      text: `Check out this ${result.title} I found on Shadesense!`,
      url: result.affiliate_url || result.url
    };
    
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareData.url);
        toast.success("Link copied to clipboard!");
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        // Fallback to clipboard
        await navigator.clipboard.writeText(shareData.url);
        toast.success("Link copied to clipboard!");
      }
    }
  };
  
  return (
    <div className="product-card group" data-testid="search-result-card">
      {/* Product Image */}
      <div className="product-card-image relative">
        {result.image_url && (
          <img src={result.image_url} alt={result.title} />
        )}
        {/* Best Match Badge */}
        {result.is_best_match && (
          <div 
            className="absolute top-2 left-2 px-2 py-1 rounded-md text-xs font-medium"
            style={{ 
              backgroundColor: 'var(--accent-primary)', 
              color: 'var(--text-inverse)' 
            }}
          >
            Best Match
          </div>
        )}
        {/* Match Percentage Badge with Extracted Color */}
        {result.match_percentage && (
          <div 
            className="absolute top-2 right-2 flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium"
            style={{ 
              backgroundColor: result.match_percentage >= 85 ? 'rgba(34, 197, 94, 0.95)' : 
                              result.match_percentage >= 75 ? 'rgba(59, 130, 246, 0.95)' : 
                              'rgba(156, 163, 175, 0.95)',
              color: '#FFFFFF'
            }}
          >
            {result.extracted_color && (
              <div 
                className="w-3 h-3 rounded-sm border border-white/30"
                style={{ backgroundColor: result.extracted_color }}
                title={`Detected: ${result.extracted_color}`}
              />
            )}
            {result.match_percentage}%
          </div>
        )}
      </div>
      
      {/* Product Content */}
      <div className="product-card-content">
        {/* Match Label */}
        <span className={`${matchLabel.className} block mb-2`}>{matchLabel.text}</span>
        
        {/* Brand/Title */}
        <h4 className="text-small font-medium line-clamp-2 mb-1" style={{ color: 'var(--text-primary)' }}>
          {result.title}
        </h4>
        
        {/* Retailer */}
        <p className="text-meta text-muted mb-3">
          Available at {result.source}
        </p>
        
        {/* Price */}
        <p className="text-body font-medium mb-4" style={{ color: 'var(--text-secondary)' }}>
          {result.price}
        </p>
        
        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleViewItem}
            disabled={isLoadingUrl}
            className="btn-primary flex-1 text-center text-small py-2 flex items-center justify-center gap-2"
            data-testid="view-item-btn"
          >
            {isLoadingUrl ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" />
                Loading...
              </>
            ) : (
              'View Item'
            )}
          </button>
          <button
            onClick={handleShare}
            className="p-2 rounded-md border transition-all hover:border-accent-primary"
            style={{ borderColor: 'var(--border-subtle)' }}
            title="Share"
          >
            <Share2 className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
          </button>
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); addToWishlist(result, category); }}
            className={`p-2 rounded-md border transition-all ${
              wishlistItems.has(result.url) 
                ? 'border-accent-primary bg-accent-muted' 
                : 'border-subtle hover:border-accent-primary'
            }`}
            style={{ borderColor: wishlistItems.has(result.url) ? 'var(--accent-primary)' : 'var(--border-subtle)' }}
          >
            <Heart 
              className={`w-4 h-4 ${wishlistItems.has(result.url) ? 'fill-current' : ''}`} 
              style={{ color: wishlistItems.has(result.url) ? 'var(--accent-primary)' : 'var(--text-muted)' }}
            />
          </button>
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [clothingCategories, setClothingCategories] = useState({});
  const [searchTypes, setSearchTypes] = useState({});
  const [selectedCategory, setSelectedCategory] = useState({});
  const [selectedSearchType, setSelectedSearchType] = useState({});
  const [searchResults, setSearchResults] = useState({});
  const [searchingItem, setSearchingItem] = useState(null);
  const [wishlistItems, setWishlistItems] = useState(new Set());
  const [activeGarmentTab, setActiveGarmentTab] = useState(0);
  const [isFromHistory, setIsFromHistory] = useState(false);
  const [selectedGender, setSelectedGender] = useState({});
  const [selectedAgeGroup, setSelectedAgeGroup] = useState({});
  const [expandedColorSearch, setExpandedColorSearch] = useState({});
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [visibleResults, setVisibleResults] = useState({}); // Track visible items per search
  const [selectedRetailer, setSelectedRetailer] = useState({}); // Track selected retailer per search
  
  // Refs for scrolling to search sections
  const searchSectionRefs = useRef({});

  useEffect(() => {
    fetchData();
    
    // Check for saved analysis passed from History page
    if (location.state?.savedAnalysis) {
      const saved = location.state.savedAnalysis;
      setAnalysisResults({
        analysis_id: saved.analysis_id,
        detected_items: saved.detected_items,
        available_categories: clothingCategories,
        search_types: searchTypes
      });
      setIsFromHistory(true);
      // Clear the state so it doesn't persist on refresh
      window.history.replaceState({}, document.title);
      return; // Don't process other image states if we have a saved analysis
    }
    
    // Check for image passed from Landing page via navigation state
    if (location.state?.uploadedImage) {
      setImage(location.state.uploadedImage);
      setImagePreview(location.state.uploadedImage);
      // Clear the state so it doesn't persist on refresh
      window.history.replaceState({}, document.title);
    }
    
    // Check for pending image from sessionStorage (after login redirect)
    const pendingImage = sessionStorage.getItem('pendingImage');
    if (pendingImage) {
      setImage(pendingImage);
      setImagePreview(pendingImage);
      sessionStorage.removeItem('pendingImage');
    }
  }, [location.state]);

  const fetchData = async () => {
    try {
      const [catRes, wishRes] = await Promise.all([
        axios.get(`${API}/clothing-categories`),
        axios.get(`${API}/wishlist`, { withCredentials: true }).catch(() => ({ data: { items: [] } }))
      ]);
      setClothingCategories(catRes.data.categories || {});
      setSearchTypes(catRes.data.search_types || {});
      setWishlistItems(new Set((wishRes.data.items || []).map(item => item.url)));
    } catch (error) {
      console.error("Failed to fetch data:", error);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setImage(reader.result);
        setImagePreview(URL.createObjectURL(file));
        setAnalysisResults(null);
        setSearchResults({});
        setSelectedCategory({});
        setSelectedSearchType({});
        setActiveGarmentTab(0);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [".jpeg", ".jpg", ".png", ".webp"] },
    maxFiles: 1
  });

  // Handle camera capture
  const handleCameraCapture = (imageData) => {
    setImage(imageData);
    setImagePreview(imageData);
    setAnalysisResults(null);
    setSearchResults({});
    setSelectedCategory({});
    setSelectedSearchType({});
    setActiveGarmentTab(0);
    toast.success("Photo captured", { description: "Ready to analyze" });
  };

  const analyzeImage = async () => {
    if (!image) return;
    setIsAnalyzing(true);
    try {
      const response = await axios.post(`${API}/analyze`, { image_base64: image }, { withCredentials: true });
      setAnalysisResults(response.data);
      toast.success("Analysis complete", { description: `Found ${response.data.detected_items.length} clothing items` });
    } catch (error) {
      toast.error("Analysis failed", { description: error.response?.data?.detail || "Please try again" });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSearch = async (itemId, color) => {
    const searchKey = `${itemId}_${color.hex}`;
    const category = selectedCategory[searchKey];
    const searchType = selectedSearchType[searchKey] || "exact";
    const gender = selectedGender[searchKey] || "all";
    const ageGroup = selectedAgeGroup[searchKey] || "all";
    const retailer = selectedRetailer[searchKey] || "all";
    
    if (!category) {
      toast.error("Please select a clothing category");
      return;
    }

    setSearchingItem(searchKey);
    
    try {
      const response = await axios.post(`${API}/search`, {
        selected_item: category,
        color_hex: color.hex,
        search_type: searchType,
        original_category: "detected",
        gender: gender,
        age_group: ageGroup,
        retailer: retailer
      }, { withCredentials: true });
      
      setSearchResults(prev => ({ ...prev, [searchKey]: response.data }));
      if (response.data.fallback_search && response.data.original_retailer) {
        toast.info(`No results from ${response.data.original_retailer}`, {
          description: `Showing ${response.data.results.length} items from all retailers instead`
        });
      } else {
        toast.success("Search complete", { description: `Found ${response.data.results.length} items` });
      }
    } catch (error) {
      toast.error("Search failed");
    } finally {
      setSearchingItem(null);
    }
  };

  // Handle "Find Similar" click - opens search for that specific color and scrolls to it
  const handleFindSimilar = (itemId, color) => {
    const searchKey = `${itemId}_${color.hex}`;
    setExpandedColorSearch(prev => ({ ...prev, [searchKey]: true }));
    
    // Scroll to the search section after a brief delay for DOM update
    setTimeout(() => {
      const element = searchSectionRefs.current[searchKey];
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Add a highlight effect
        element.classList.add('ring-2', 'ring-offset-2');
        element.style.setProperty('--tw-ring-color', 'var(--accent-primary)');
        setTimeout(() => {
          element.classList.remove('ring-2', 'ring-offset-2');
        }, 2000);
      }
    }, 100);
    
    toast.info("Select options below", { description: "Choose a category and click Search" });
  };

  const addToWishlist = async (result, category) => {
    try {
      await axios.post(`${API}/wishlist`, {
        title: result.title,
        url: result.url,
        affiliate_url: result.affiliate_url,
        image_url: result.image_url,
        price: result.price,
        source: result.source,
        color_hex: result.color_searched || "",
        category: category
      }, { withCredentials: true });
      setWishlistItems(prev => new Set([...prev, result.url]));
      toast.success("Saved to wishlist");
    } catch (error) {
      toast.error("Failed to save");
    }
  };

  const trackClick = async (result) => {
    try {
      await axios.post(`${API}/track-click`, {
        product_url: result.url,
        affiliate_url: result.affiliate_url || result.url,
        source: result.source,
        color_hex: result.color_searched,
        category: result.color_type
      }, { withCredentials: true });
    } catch (error) {
      console.error("Failed to track click");
    }
  };

  const shareAnalysis = async () => {
    if (!analysisResults) return;
    try {
      const response = await axios.post(`${API}/share/analysis/${analysisResults.analysis_id}`, {}, { withCredentials: true });
      const shareUrl = `${window.location.origin}${response.data.share_url}`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied!");
    } catch (error) {
      toast.error("Failed to create share link");
    }
  };

  const clearImage = () => {
    setImage(null);
    setImagePreview(null);
    setAnalysisResults(null);
    setSearchResults({});
    setSelectedCategory({});
    setSelectedSearchType({});
    setActiveGarmentTab(0);
    setIsFromHistory(false);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-primary)' }} data-testid="dashboard">
      <main className="container py-12">
        <div className="max-w-5xl mx-auto">
          
          {/* Upload Section - Only show if no analysis results */}
          {!analysisResults && (
            <div className="text-center mb-12 animate-fade-in">
              <h1 className="text-h1 mb-4" style={{ color: 'var(--text-primary)' }}>
                Analyze Your Clothes
              </h1>
              <p className="text-body text-secondary max-w-lg mx-auto">
                We&apos;ll analyze the colors in your clothing and help you find matching pieces.
              </p>
            </div>
          )}

          {/* Upload Card - Only show if no image and no saved analysis */}
          {!imagePreview && !analysisResults ? (
            <div className="max-w-lg mx-auto animate-fade-in">
              {/* Drag & Drop Upload Card */}
              <div className="card-glass p-12 text-center">
                {/* Main Upload Zone - Supports drag & drop */}
                <div 
                  {...getRootProps()}
                  className={`upload-zone w-full cursor-pointer ${isDragActive ? 'active' : ''}`}
                  data-testid="upload-zone"
                >
                  <input {...getInputProps()} />
                  <div 
                    className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center transition-transform"
                    style={{ 
                      backgroundColor: isDragActive ? 'var(--accent-primary)' : 'var(--accent-muted)',
                      transform: isDragActive ? 'scale(1.1)' : 'scale(1)'
                    }}
                  >
                    <Camera 
                      className="w-8 h-8 transition-colors" 
                      style={{ color: isDragActive ? 'var(--text-inverse)' : 'var(--accent-primary)' }} 
                    />
                  </div>
                  <p className="text-body mb-2" style={{ color: 'var(--text-primary)' }}>
                    {isDragActive ? "Drop your image here" : "Snap a photo of your clothes"}
                  </p>
                  <p 
                    className="text-small underline hover:opacity-80 transition-opacity"
                    style={{ color: 'var(--accent-primary)' }}
                  >
                    or drag & drop / browse your photos
                  </p>
                </div>
                
                {/* Camera Button - Only show on mobile/touch devices */}
                <button 
                  onClick={() => setIsCameraOpen(true)}
                  className="btn-secondary w-full flex items-center justify-center gap-2 mt-6 md:hidden"
                  data-testid="camera-btn"
                >
                  <Camera className="w-4 h-4" />
                  Open Camera
                </button>
              </div>
              
              {/* Camera Modal */}
              <CameraCapture 
                isOpen={isCameraOpen}
                onClose={() => setIsCameraOpen(false)}
                onCapture={handleCameraCapture}
              />
            </div>
          ) : imagePreview && !isFromHistory ? (
            /* Only show uploaded image card if we have an image AND it's not from history */
            <div className="animate-fade-in">
              {/* Uploaded Image Preview */}
              <div className="card max-w-lg mx-auto mb-8 relative">
                <img 
                  src={imagePreview} 
                  alt="Uploaded clothing" 
                  className="w-full rounded-lg"
                  data-testid="uploaded-image" 
                />
                <button 
                  onClick={clearImage}
                  className="absolute top-4 right-4 p-2 rounded-full transition-all hover:scale-105"
                  style={{ backgroundColor: 'var(--surface-primary)', boxShadow: 'var(--shadow-md)' }}
                >
                  <X className="w-4 h-4" style={{ color: 'var(--text-primary)' }} />
                </button>
              </div>

              {/* Analyze Button */}
              {!analysisResults && (
                <div className="text-center">
                  <button 
                    onClick={analyzeImage} 
                    disabled={isAnalyzing}
                    className="btn-primary"
                    data-testid="analyze-btn"
                  >
                    {isAnalyzing ? (
                      <>
                        <span className="animate-shimmer inline-block w-24 h-4 rounded mr-2"></span>
                        Refining color details...
                      </>
                    ) : (
                      'Analyze Colors'
                    )}
                  </button>
                </div>
              )}
            </div>
          ) : null}

          {/* Analysis Results */}
          {analysisResults && (
            <div className="mt-12 animate-fade-in">
              {/* From History Banner */}
              {isFromHistory && (
                <div 
                  className="mb-6 p-4 rounded-lg flex items-center gap-3"
                  style={{ backgroundColor: 'var(--accent-muted)' }}
                >
                  <History className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
                  <div className="flex-1">
                    <p className="text-small font-medium" style={{ color: 'var(--text-primary)' }}>
                      Viewing saved analysis
                    </p>
                    <p className="text-meta text-secondary">
                      Continue searching for matching items or start a new analysis
                    </p>
                  </div>
                  <button onClick={clearImage} className="btn-secondary text-small">
                    New Analysis
                  </button>
                </div>
              )}

              {/* Header with Share */}
              <div className="flex items-center justify-between mb-8">
                <div>
                  <p className="text-meta uppercase tracking-widest mb-2" style={{ color: 'var(--accent-primary)' }}>
                    Color Analysis
                  </p>
                  <h2 className="text-h2" style={{ color: 'var(--text-primary)' }}>
                    Detected Items
                  </h2>
                </div>
                <button 
                  onClick={shareAnalysis}
                  className="btn-secondary flex items-center gap-2"
                >
                  <Share2 className="w-4 h-4" />
                  Share
                </button>
              </div>

              {/* Garment Tabs - More Prominent */}
              {analysisResults.detected_items.length > 1 && (
                <div className="mb-10">
                  <p className="text-meta uppercase tracking-widest mb-4" style={{ color: 'var(--text-muted)' }}>
                    Select Garment
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {analysisResults.detected_items.map((item, index) => (
                      <button
                        key={item.item_id}
                        onClick={() => setActiveGarmentTab(index)}
                        className={`px-6 py-3 rounded-xl text-small font-medium transition-all ${
                          activeGarmentTab === index 
                            ? 'shadow-md' 
                            : 'hover:shadow-sm'
                        }`}
                        style={{
                          backgroundColor: activeGarmentTab === index ? 'var(--accent-primary)' : 'var(--surface-primary)',
                          color: activeGarmentTab === index ? 'var(--text-inverse)' : 'var(--text-primary)',
                          border: activeGarmentTab === index ? 'none' : '2px solid var(--border-subtle)'
                        }}
                      >
                        {item.category || item.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Active Garment Details */}
              {analysisResults.detected_items.map((item, itemIndex) => (
                <div 
                  key={item.item_id} 
                  className={itemIndex === activeGarmentTab ? 'block' : 'hidden'}
                  data-testid={`detected-item-${itemIndex}`}
                >
                  {/* Item Header */}
                  <div className="card mb-8">
                    <span className="text-meta uppercase tracking-widest block mb-2" style={{ color: 'var(--accent-primary)' }}>
                      {item.category}
                    </span>
                    <h3 className="text-h3 mb-2" style={{ color: 'var(--text-primary)' }}>
                      {item.name}
                    </h3>
                    {item.description && (
                      <p className="text-small text-secondary">{item.description}</p>
                    )}
                  </div>

                  {/* Color Palette - Hero Section */}
                  <div className="mb-12">
                    <h4 className="text-small uppercase tracking-widest mb-6 text-secondary">
                      Color Palette — Click any color to search
                    </h4>
                    <div className="flex flex-wrap gap-6">
                      {item.colors.map((color, colorIndex) => {
                        const searchKey = `${item.item_id}_${color.hex}`;
                        const isExpanded = expandedColorSearch[searchKey];
                        const hasResults = searchResults[searchKey];
                        return (
                          <div 
                            key={colorIndex} 
                            className="flex items-center gap-4"
                            data-testid={`color-swatch-${itemIndex}-${colorIndex}`}
                          >
                            <div 
                              className="color-swatch cursor-pointer hover:scale-110 transition-transform"
                              style={{ backgroundColor: color.hex }}
                              onClick={() => handleFindSimilar(item.item_id, color)}
                            />
                            <div>
                              <p className="text-body font-medium" style={{ color: 'var(--text-primary)' }}>
                                {color.pantone_name || color.name}
                              </p>
                              <p className="text-meta text-muted">{color.hex}</p>
                              <button 
                                onClick={() => handleFindSimilar(item.item_id, color)}
                                className="text-meta mt-1 hover:underline"
                                style={{ color: 'var(--accent-primary)' }}
                              >
                                {hasResults ? `${hasResults.results.length} matches found ↓` : isExpanded ? 'Select options below ↓' : 'Find Similar →'}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Search Section for Each Color - Show for all colors */}
                  {item.colors.map((color, colorIndex) => {
                    const searchKey = `${item.item_id}_${color.hex}`;
                    const isExpanded = expandedColorSearch[searchKey] || searchResults[searchKey] || colorIndex < 2;
                    
                    if (!isExpanded) return null;
                    
                    return (
                      <div 
                        key={colorIndex} 
                        ref={el => searchSectionRefs.current[searchKey] = el}
                        className="card mb-8 animate-fade-in transition-all duration-300"
                      >
                        {/* Color Header */}
                        <div className="flex items-center gap-3 mb-6">
                          <div 
                            className="w-10 h-10 rounded-lg shadow-sm"
                            style={{ backgroundColor: color.hex, border: '1px solid var(--border-subtle)' }}
                          />
                          <div className="flex-1">
                            <p className="text-body font-medium" style={{ color: 'var(--text-primary)' }}>
                              {color.pantone_name || color.name}
                            </p>
                            <p className="text-meta text-muted">Find matching items in this color</p>
                          </div>
                          {colorIndex >= 2 && (
                            <button
                              onClick={() => {
                                setExpandedColorSearch(prev => ({ ...prev, [searchKey]: false }));
                                setSearchResults(prev => {
                                  const newResults = { ...prev };
                                  delete newResults[searchKey];
                                  return newResults;
                                });
                              }}
                              className="p-2 rounded-lg hover:bg-black/5"
                            >
                              <X className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                            </button>
                          )}
                        </div>
                        
                        {/* Search Controls - Enhanced with Gender, Age, and Retailer */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
                          {/* Category Dropdown */}
                          <Select
                            value={selectedCategory[searchKey] || ""}
                            onValueChange={(value) => setSelectedCategory(prev => ({ ...prev, [searchKey]: value }))}
                          >
                            <SelectTrigger 
                              className="h-11 border rounded-lg"
                              style={{ 
                                backgroundColor: 'var(--surface-secondary)', 
                                borderColor: 'var(--border-subtle)',
                                color: 'var(--text-primary)'
                              }}
                              data-testid={`category-select-${itemIndex}-${colorIndex}`}
                            >
                              <SelectValue placeholder="Find a matching..." />
                            </SelectTrigger>
                            <SelectContent 
                              className="rounded-lg max-h-64"
                              style={{ 
                                backgroundColor: 'var(--surface-primary)', 
                                borderColor: 'var(--border-subtle)'
                              }}
                            >
                              {Object.entries(clothingCategories).map(([cat, items]) => (
                                <div key={cat}>
                                  <div 
                                    className="px-3 py-2 text-meta uppercase tracking-widest"
                                    style={{ color: 'var(--text-muted)' }}
                                  >
                                    {cat}
                                  </div>
                                  {items.map((clothingItem) => (
                                    <SelectItem 
                                      key={clothingItem} 
                                      value={clothingItem}
                                      className="text-small"
                                    >
                                      {clothingItem}
                                    </SelectItem>
                                  ))}
                                </div>
                              ))}
                            </SelectContent>
                          </Select>

                          {/* Search Type Dropdown */}
                          <Select
                            value={selectedSearchType[searchKey] || "exact"}
                            onValueChange={(value) => setSelectedSearchType(prev => ({ ...prev, [searchKey]: value }))}
                          >
                            <SelectTrigger 
                              className="h-11 border rounded-lg"
                              style={{ 
                                backgroundColor: 'var(--surface-secondary)', 
                                borderColor: 'var(--border-subtle)',
                                color: 'var(--text-primary)'
                              }}
                              data-testid={`search-type-select-${itemIndex}-${colorIndex}`}
                            >
                              <SelectValue placeholder="Search type..." />
                            </SelectTrigger>
                            <SelectContent 
                              className="rounded-lg"
                              style={{ 
                                backgroundColor: 'var(--surface-primary)', 
                                borderColor: 'var(--border-subtle)'
                              }}
                            >
                              <SelectItem value="exact" className="text-small">
                                <div>
                                  <span style={{ color: 'var(--text-primary)' }}>Exact Color Match</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="complementary" className="text-small">
                                <div>
                                  <span style={{ color: 'var(--text-primary)' }}>Complementary Colors</span>
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>

                          {/* Gender Dropdown */}
                          <Select
                            value={selectedGender[searchKey] || "all"}
                            onValueChange={(value) => setSelectedGender(prev => ({ ...prev, [searchKey]: value }))}
                          >
                            <SelectTrigger 
                              className="h-11 border rounded-lg"
                              style={{ 
                                backgroundColor: 'var(--surface-secondary)', 
                                borderColor: 'var(--border-subtle)',
                                color: 'var(--text-primary)'
                              }}
                            >
                              <div className="flex items-center gap-2">
                                <Users className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                                <SelectValue placeholder="Gender" />
                              </div>
                            </SelectTrigger>
                            <SelectContent 
                              className="rounded-lg"
                              style={{ 
                                backgroundColor: 'var(--surface-primary)', 
                                borderColor: 'var(--border-subtle)'
                              }}
                            >
                              {GENDER_OPTIONS.map((option) => (
                                <SelectItem key={option.value} value={option.value} className="text-small">
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          {/* Age Group Dropdown */}
                          <Select
                            value={selectedAgeGroup[searchKey] || "all"}
                            onValueChange={(value) => setSelectedAgeGroup(prev => ({ ...prev, [searchKey]: value }))}
                          >
                            <SelectTrigger 
                              className="h-11 border rounded-lg"
                              style={{ 
                                backgroundColor: 'var(--surface-secondary)', 
                                borderColor: 'var(--border-subtle)',
                                color: 'var(--text-primary)'
                              }}
                            >
                              <div className="flex items-center gap-2">
                                <Baby className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                                <SelectValue placeholder="Age Group" />
                              </div>
                            </SelectTrigger>
                            <SelectContent 
                              className="rounded-lg"
                              style={{ 
                                backgroundColor: 'var(--surface-primary)', 
                                borderColor: 'var(--border-subtle)'
                              }}
                            >
                              {AGE_GROUP_OPTIONS.map((option) => (
                                <SelectItem key={option.value} value={option.value} className="text-small">
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          {/* Retailer Dropdown */}
                          <Select
                            value={selectedRetailer[searchKey] || "all"}
                            onValueChange={(value) => setSelectedRetailer(prev => ({ ...prev, [searchKey]: value }))}
                          >
                            <SelectTrigger 
                              className="h-11 border rounded-lg"
                              style={{ 
                                backgroundColor: 'var(--surface-secondary)', 
                                borderColor: 'var(--border-subtle)',
                                color: 'var(--text-primary)'
                              }}
                            >
                              <div className="flex items-center gap-2">
                                <Store className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                                <SelectValue placeholder="Retailer" />
                              </div>
                            </SelectTrigger>
                            <SelectContent 
                              className="rounded-lg max-h-80"
                              style={{ 
                                backgroundColor: 'var(--surface-primary)', 
                                borderColor: 'var(--border-subtle)'
                              }}
                            >
                              <SelectItem value="all" className="text-small font-medium">
                                Search All Retailers
                              </SelectItem>
                              {/* Group retailers by category */}
                              {['Marketplaces', 'Department Stores', 'Fast Fashion', 'Sportswear', 'Premium Brands', 'Luxury', 'Casual', 'Outdoor', "Men's", "Women's", 'Value'].map(category => (
                                <div key={category}>
                                  <div 
                                    className="px-3 py-2 text-meta uppercase tracking-widest sticky top-0"
                                    style={{ color: 'var(--text-muted)', backgroundColor: 'var(--surface-primary)' }}
                                  >
                                    {category}
                                  </div>
                                  {RETAILER_OPTIONS.filter(r => r.category === category).map((retailer) => (
                                    <SelectItem 
                                      key={retailer.value} 
                                      value={retailer.value}
                                      className="text-small"
                                    >
                                      {retailer.label}
                                    </SelectItem>
                                  ))}
                                </div>
                              ))}
                            </SelectContent>
                          </Select>

                          {/* Search Button */}
                          <button
                            onClick={() => handleSearch(item.item_id, color)}
                            disabled={!selectedCategory[searchKey] || searchingItem === searchKey}
                            className="btn-accent h-11 flex items-center justify-center gap-2"
                            data-testid={`search-btn-${itemIndex}-${colorIndex}`}
                          >
                            {searchingItem === searchKey ? (
                              <span className="animate-shimmer w-16 h-4 rounded"></span>
                            ) : (
                              <>
                                <Search className="w-4 h-4" />
                                Search
                              </>
                            )}
                          </button>
                        </div>

                        {/* Search Results - Curated Grid */}
                        {searchResults[searchKey] && (
                          <div className="animate-fade-in">
                            {/* Fallback Search Notice */}
                            {searchResults[searchKey].fallback_search && searchResults[searchKey].original_retailer && (
                              <div className="mb-4 p-4 rounded-lg flex items-start gap-3" style={{ backgroundColor: 'rgba(234, 179, 8, 0.08)', border: '1px solid rgba(234, 179, 8, 0.2)' }} data-testid="fallback-search-banner">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="rgb(161, 128, 17)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                                <div>
                                  <p className="text-small font-medium" style={{ color: 'var(--text-primary)' }}>
                                    No matches found from <strong>{searchResults[searchKey].original_retailer}</strong>
                                  </p>
                                  <p className="text-meta mt-1" style={{ color: 'var(--text-secondary)' }}>
                                    We widened your search and found {searchResults[searchKey].results.length} items from other retailers to consider.
                                  </p>
                                </div>
                              </div>
                            )}

                            {/* Affiliate Disclosure */}
                            <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: 'var(--accent-muted)' }}>
                              <p className="text-small italic" style={{ color: 'var(--text-secondary)' }}>
                                Some links are affiliate links. We may earn a commission at no extra cost to you.
                              </p>
                            </div>
                            
                            <div className="flex flex-wrap items-center justify-between gap-3 mb-6 pb-4" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="text-small text-secondary">
                                  {searchResults[searchKey].results.length} curated items
                                </p>
                                {selectedSearchType[searchKey] !== "complementary" && searchResults[searchKey].results.length > 0 && (
                                  <span className="px-2 py-0.5 rounded text-meta" style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', color: 'rgb(34, 197, 94)' }}>
                                    70%+ color match
                                  </span>
                                )}
                                {selectedGender[searchKey] && selectedGender[searchKey] !== 'all' && (
                                  <span className="px-2 py-0.5 rounded text-meta" style={{ backgroundColor: 'var(--accent-muted)' }}>
                                    {GENDER_OPTIONS.find(o => o.value === selectedGender[searchKey])?.label}
                                  </span>
                                )}
                                {selectedAgeGroup[searchKey] && selectedAgeGroup[searchKey] !== 'all' && (
                                  <span className="px-2 py-0.5 rounded text-meta" style={{ backgroundColor: 'var(--accent-muted)' }}>
                                    {AGE_GROUP_OPTIONS.find(o => o.value === selectedAgeGroup[searchKey])?.label}
                                  </span>
                                )}
                              </div>
                              <span className="text-meta" style={{ color: 'var(--accent-primary)' }}>
                                {selectedSearchType[searchKey] === "complementary" ? "Complementary Colors" : "Exact Match"}
                              </span>
                            </div>
                            
                            {searchResults[searchKey].results.length === 0 ? (
                              <div className="text-center py-8">
                                <p className="text-small text-secondary mb-2">No items found with 70%+ color match</p>
                                <p className="text-meta text-muted">Try searching for complementary colors instead</p>
                              </div>
                            ) : (
                              <>
                                {/* 2-3 Column Grid */}
                                <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
                                  {searchResults[searchKey].results.slice(0, visibleResults[searchKey] || 6).map((result, rIndex) => (
                                    <ProductCard 
                                      key={rIndex} 
                                      result={result} 
                                      category={item.category}
                                      wishlistItems={wishlistItems}
                                      trackClick={trackClick}
                                      addToWishlist={addToWishlist}
                                    />
                                  ))}
                                </div>

                                {/* View More Button */}
                                {searchResults[searchKey].results.length > (visibleResults[searchKey] || 6) && (
                                  <div className="text-center mt-8">
                                    <button 
                                      className="btn-secondary"
                                      onClick={() => setVisibleResults(prev => ({
                                        ...prev,
                                        [searchKey]: (prev[searchKey] || 6) + 6
                                      }))}
                                    >
                                      View More Matches ({searchResults[searchKey].results.length - (visibleResults[searchKey] || 6)} remaining)
                                    </button>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}

              {/* New Analysis Button */}
              <div className="text-center mt-12">
                <button onClick={clearImage} className="btn-secondary">
                  Analyze Another Image
                </button>
              </div>
            </div>
          )}

        </div>
      </main>
    </div>
  );
};

export default Dashboard;
